import { MailWarning } from 'lucide-react';
import React, { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRestClient } from '../../api/RestClientContext';
import { AccountBindResponse } from '../../models/auth';
import LogoBar from '../../shared/components/LogoBar';
import { notify } from '../../shared/utils/notification';
import '../auth/LoginScreen.css';
import './AccountScreen.css';

export const AccountBindEmailScreen: React.FC = () => {
    const client = useRestClient();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [bindToken, setBindToken] = useState<AccountBindResponse | null>(null);
    const [expiresAt, setExpiresAt] = useState<number | null>(null);
    const [remainingSeconds, setRemainingSeconds] = useState(0);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!expiresAt) return;
        const updateRemainingTime = () => setRemainingSeconds(Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000)));
        updateRemainingTime();
        const timer = window.setInterval(updateRemainingTime, 1000);
        return () => window.clearInterval(timer);
    }, [expiresAt]);

    const bindEmail = async (event: FormEvent) => {
        event.preventDefault();
        const normalizedEmail = email.trim().toLowerCase();
        if (!normalizedEmail.trim() || !normalizedEmail.includes('@')) {
            notify.error('Введите корректный email');
            return;
        }
        if (normalizedEmail.endsWith('@gmail.com') || normalizedEmail.endsWith('@googlemail.com')) {
            notify.warn('Адреса Google (Gmail) не поддерживаются 🥲 попробуйте привязать другую почту');
            return;
        }
        setLoading(true);
        try {
            const result = await client.bindEmail({ identity: normalizedEmail.trim(), type: 'EMAIL' });
            if (result.success) {
                setBindToken({ rawToken: result.result.rawToken, tokenExpireTimeMinutes: result.result.tokenExpireTimeMinutes });
                setExpiresAt(Date.now() + result.result.tokenExpireTimeMinutes * 60 * 1000);
            }
        } finally {
            setLoading(false);
        }
    };

    const verifyEmail = async (event: FormEvent) => {
        event.preventDefault();
        if (!/^\d{6}$/.test(code)) {
            notify.error('Введите 6-значный код');
            return;
        }
        if (remainingSeconds === 0) {
            notify.error('Срок действия кода истёк, запросите новый код');
            return;
        }
        setLoading(true);
        try {
            const result = await client.verifyEmailBinding({ rawToken: bindToken!.rawToken, code });
            if (result.success) {
                notify.success('Email успешно привязан');
                navigate('/account');
            }
        } finally {
            setLoading(false);
        }
    };

    const restart = () => {
        setBindToken(null);
        setCode('');
        setExpiresAt(null);
        setRemainingSeconds(0);
    };

    return (
        <main className="account-screen">
            <LogoBar />
            <section className="account-panel glass-ui">
                <button className="account-back-link" type="button" onClick={() => navigate('/account')}>Назад к аккаунту</button>
                <span className="email-auth-kicker">ACCOUNT / EMAIL</span>
                <h1>Привязка email</h1>
                <p>{bindToken ? `Код отправлен на ${email}` : 'Добавьте email, чтобы входить в Ogo Secure Access из обычного браузера.'}</p>
                {bindToken && (
                    <div className="account-spam-notice" role="alert">
                        <MailWarning size={22} aria-hidden="true" />
                        <span>Если письма нет во входящих, обязательно проверьте папку «Спам».</span>
                    </div>
                )}
                {!bindToken ? (
                    <form onSubmit={bindEmail}>
                        <label htmlFor="account-email">Email</label>
                        <input id="account-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" placeholder="you@example.com" required />
                        <button className="email-auth-submit" type="submit" disabled={loading}>{loading ? 'ОТПРАВЛЯЕМ...' : 'ПРИВЯЗАТЬ EMAIL'}</button>
                    </form>
                ) : (
                    <form onSubmit={verifyEmail}>
                        <label htmlFor="account-code">Код из письма</label>
                        <input id="account-code" inputMode="numeric" value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} autoComplete="one-time-code" placeholder="000000" required />
                        <div className={`account-code-timer ${remainingSeconds === 0 ? 'expired' : ''}`}>
                            {remainingSeconds > 0 ? `Код действителен ещё ${Math.floor(remainingSeconds / 60)}:${String(remainingSeconds % 60).padStart(2, '0')}` : 'Срок действия кода истёк'}
                        </div>
                        {remainingSeconds > 0 && <button className="email-auth-submit" type="submit" disabled={loading}>{loading ? 'ПРОВЕРЯЕМ...' : 'ПОДТВЕРДИТЬ'}</button>}
                        <button className="email-auth-link" type="button" onClick={restart}>Запросить новый код</button>
                    </form>
                )}
            </section>
        </main>
    );
};
